import { useState } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const resultTypeLabels = {
  staff: 'Staff',
  observations: 'Observations',
  compliance: 'Compliance',
  tasks: 'Tasks',
  candidates: 'Candidates',
};

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (query.length < 2) {
      toast.error('Enter at least 2 characters');
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get('/search', { params: { q: query } });
      setResults(data);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const totalResults = results
    ? Object.values(results).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)
    : 0;

  return (
    <div className="animate-slide-up">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Global Search</h1>
      <p className="text-gray-500 text-sm mb-6">Search across all records — staff, observations, compliance, tasks, and candidates.</p>

      <form onSubmit={search} className="flex gap-3 mb-8">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, keyword, type..."
            className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm bg-white"
          />
        </div>
        <button
          type="submit"
          disabled={searching}
          className="bg-orange-500 text-white px-6 py-3 rounded-2xl hover:bg-orange-600 transition-colors font-medium disabled:opacity-50"
        >
          {searching ? 'Searching...' : 'Search'}
        </button>
      </form>

      {results && (
        <div>
          <p className="text-sm text-gray-500 mb-4">{totalResults} result{totalResults !== 1 ? 's' : ''} found</p>

          {Object.entries(results).map(([type, items]) => {
            if (!Array.isArray(items) || items.length === 0) return null;
            return (
              <div key={type} className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">{resultTypeLabels[type] || type}</h2>
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="divide-y divide-gray-50">
                    {items.map((item, i) => (
                      <div key={i} className="px-6 py-3 hover:bg-gray-50 transition-colors">
                        <ResultItem type={type} item={item} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          {totalResults === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
              <p className="text-gray-500">No results found for "{query}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultItem({ type, item }) {
  switch (type) {
    case 'staff':
      return (
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{item.first_name} {item.last_name}</p>
            <p className="text-xs text-gray-500">{item.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={item.role} />
            <StatusBadge status={item.status} />
          </div>
        </div>
      );
    case 'observations':
      return (
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{item.staff_name}</p>
            <p className="text-xs text-gray-500">{item.classroom_name} &middot; {new Date(item.observation_date).toLocaleDateString()}</p>
          </div>
          <div className="flex items-center gap-2">
            {item.overall_score && <span className="text-sm font-bold">{item.overall_score}/5</span>}
            <StatusBadge status={item.status} />
          </div>
        </div>
      );
    case 'compliance':
      return (
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{item.type_name}</p>
            <p className="text-xs text-gray-500">{item.staff_name || 'Center-wide'}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={item.tier} />
            <StatusBadge status={item.status} />
          </div>
        </div>
      );
    case 'tasks':
      return (
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{item.title}</p>
            <p className="text-xs text-gray-500">{item.assigned_to_name} &middot; {item.area}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={item.priority} />
            <StatusBadge status={item.status} />
          </div>
        </div>
      );
    case 'candidates':
      return (
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{item.first_name} {item.last_name}</p>
            <p className="text-xs text-gray-500">{item.role_applied} &middot; {item.email}</p>
          </div>
          <StatusBadge status={item.stage} />
        </div>
      );
    default:
      return <p className="text-sm text-gray-600">{JSON.stringify(item)}</p>;
  }
}
